import { Controller } from "@hotwired/stimulus"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import Link from "@tiptap/extension-link"

import { newsletterExtensions } from "../newsletter_editor/schema"
import { uploadCompressedImage } from "../newsletter_editor/image_upload"
import { MAX_IMAGE_ROW_ITEMS } from "../newsletter_editor/image_row"

// Given an array of `{url, width, height}` uploads, return `{src, alt,
// widthPercent}` entries whose widthPercents are proportional to each
// image's aspect ratio (width/height). When the row uses these widths
// each image lands at the same rendered height with no cropping.
//
// Falls back to even distribution when any item is missing dimensions.
function aspectScaledRowItems(uploads) {
  const ratios = uploads.map((u) => {
    if (!u.width || !u.height) return null
    return u.width / u.height
  })
  const allKnown = ratios.every((r) => r && Number.isFinite(r) && r > 0)
  if (!allKnown) {
    return uploads.map((u) => ({ src: u.url, alt: "" }))
  }
  const sum = ratios.reduce((a, b) => a + b, 0)
  return uploads.map((u, i) => ({
    src: u.url,
    alt: "",
    widthPercent: Math.round((ratios[i] / sum) * 100),
  }))
}

// Phase 1 Tiptap-based newsletter editor.
//
// Responsibilities:
// - Mount a Tiptap editor instance on the `editor` target.
// - Keep `contentJsonInput` (canonical) and `bodyInput` (cached HTML for
//   back-compat with existing mailer) in sync on every transaction.
// - Expose toolbar actions: paragraph, heading 1/2/3, bold, italic, link.
//
// Out of scope for Phase 1: autosave, custom block nodes, drag/drop, colors,
// alignment, font size, lists. Those are subsequent phases.

export default class extends Controller {
  static targets = [
    "editor",
    "contentJsonInput",
    "bodyInput",
    "subjectInput",
    "formatSelect",
    "boldButton",
    "italicButton",
    "linkButton",
    "fontSizeSelect",
    "textColorSwatch",
    "highlightSwatch",
    "alignLeftButton",
    "alignCenterButton",
    "alignRightButton",
    "fileInput",
    "multiFileInput",
    "imageButton",
    "saveStatus",
    "newsletterBgSwatch",
    "bgColorInput",
    "fontFamilyInput",
    "fontFamilySelect",
    "surroundings",
    "wrapper",
    "toolbar",
    "toolbarSentinel",
    "blockMenu",
  ]

  static values = {
    initialContent: { type: Object, default: {} },
    legacyBody: { type: String, default: "" },
    autosaveUrl: { type: String, default: "" },
    imageUploadUrl: { type: String, default: "" },
    persisted: { type: Boolean, default: false },
    lockVersion: { type: Number, default: 0 },
  }

  connect() {
    console.info(
      "%c[newsletter-editor] Tiptap controller mounted (Phase 3.1)",
      "color:#2563eb;font-weight:bold;"
    )

    const initial = this.#initialDocOrFallback()

    this.editor = new Editor({
      element: this.editorTarget,
      extensions: [
        StarterKit.configure({
          // We provide our own document, paragraph, and heading.
          document: false,
          paragraph: false,
          heading: false,
          // Phase 1 doesn't ship these yet.
          bulletList: false,
          orderedList: false,
          listItem: false,
          blockquote: false,
          codeBlock: false,
          code: false,
          horizontalRule: false,
          strike: false,
        }),
        ...newsletterExtensions,
        Link.configure({
          openOnClick: false,
          autolink: true,
          protocols: ["http", "https", "mailto"],
          HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" },
        }),
      ],
      content: initial,
      editorProps: {
        attributes: {
          class: "tiptap-newsletter-editor",
        },
      },
      onUpdate: () => {
        this.#sync()
        this.#scheduleAutosave()
      },
      onSelectionUpdate: () => this.#refreshToolbarState(),
      onTransaction: () => this.#refreshToolbarState(),
    })

    this.#sync()
    this.#refreshToolbarState()
    this.#wireRowAddCallback()
    this.#wireStickyToolbar()
    this.#refreshImageInsertionAvailability()
  }

  // Subject is outside the editor view but inside the controller scope so
  // any change must funnel through autosave alongside the editor state.
  subjectChanged() {
    this.#scheduleAutosave()
  }

  disconnect() {
    if (this.autosaveTimer) {
      clearTimeout(this.autosaveTimer)
      this.autosaveTimer = null
    }
    if (this.toolbarObserver) {
      this.toolbarObserver.disconnect()
      this.toolbarObserver = null
    }
    if (this.blockMenuOutsideClose) {
      document.removeEventListener("mousedown", this.blockMenuOutsideClose, true)
      this.blockMenuOutsideClose = null
    }
    if (this.editor) {
      this.editor.destroy()
      this.editor = null
    }
  }

  // --- Toolbar actions ----------------------------------------------------

  bold(event) {
    event?.preventDefault()
    this.editor?.chain().focus().toggleBold().run()
  }

  italic(event) {
    event?.preventDefault()
    this.editor?.chain().focus().toggleItalic().run()
  }

  setFormat(event) {
    const value = event.target.value
    if (!this.editor) return
    if (value === "paragraph") {
      this.editor.chain().focus().setNewsletterParagraph().run()
      return
    }
    const match = value.match(/^h([1-3])$/)
    if (match) {
      this.editor.chain().focus().setNewsletterHeading({ level: Number(match[1]) }).run()
    }
  }

  link(event) {
    event?.preventDefault()
    if (!this.editor) return
    const previous = this.editor.getAttributes("link").href || ""
    const url = window.prompt("Link URL", previous)
    if (url === null) return
    if (url === "") {
      this.editor.chain().focus().extendMarkRange("link").unsetLink().run()
      return
    }
    this.editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url })
      .run()
  }

  // Reads `data-color` (palette button) or `event.target.value` (color input).
  setTextColor(event) {
    const color = this.#colorFromEvent(event)
    if (!color || !this.editor) return
    this.editor.chain().focus().setColor(color).run()
    this.#closePaletteFor(event)
  }

  setHighlight(event) {
    const color = this.#colorFromEvent(event)
    if (!color || !this.editor) return
    this.editor.chain().focus().setHighlight({ color }).run()
    this.#closePaletteFor(event)
  }

  clearFormatting(event) {
    event?.preventDefault()
    if (!this.editor) return
    this.editor.chain().focus().unsetColor().unsetHighlight().unsetFontSize().unsetFontFamily().run()
  }

  setFontSize(event) {
    if (!this.editor) return
    const value = event.target.value
    if (!value) {
      this.editor.chain().focus().unsetFontSize().run()
      return
    }
    const size = /\D/.test(value) ? value : `${value}px`
    this.editor.chain().focus().setFontSize(size).run()
  }

  alignLeft(event) {
    event?.preventDefault()
    this.editor?.chain().focus().setTextAlign("left").run()
  }

  alignCenter(event) {
    event?.preventDefault()
    this.editor?.chain().focus().setTextAlign("center").run()
  }

  alignRight(event) {
    event?.preventDefault()
    this.editor?.chain().focus().setTextAlign("right").run()
  }

  // --- Block insertion ----------------------------------------------------

  insertDivider(event) {
    event?.preventDefault()
    this.#closeBlockMenu()
    this.editor?.chain().focus().insertNewsletterDivider().run()
  }

  insertParagraph(event) {
    event?.preventDefault()
    this.#closeBlockMenu()
    this.editor?.chain().focus().insertNewsletterParagraphAfter().run()
  }

  insertHeading(event) {
    event?.preventDefault()
    const level = Number(event.currentTarget?.dataset?.level || 2)
    this.#closeBlockMenu()
    this.editor?.chain().focus().insertNewsletterHeadingAfter(level).run()
  }

  insertTextBox(event) {
    event?.preventDefault()
    this.#closeBlockMenu()
    this.editor?.chain().focus().insertNewsletterTextBox().run()
  }

  toggleBlockMenu(event) {
    event?.preventDefault()
    event?.stopPropagation()
    if (!this.hasBlockMenuTarget) return
    const open = this.blockMenuTarget.style.display === "grid"
    if (open) {
      this.#closeBlockMenu()
    } else {
      this.blockMenuTarget.style.display = "grid"
      this.#installBlockMenuOutsideClose(event?.currentTarget)
    }
  }

  #closeBlockMenu() {
    if (this.hasBlockMenuTarget) this.blockMenuTarget.style.display = "none"
    if (this.blockMenuOutsideClose) {
      document.removeEventListener("mousedown", this.blockMenuOutsideClose, true)
      this.blockMenuOutsideClose = null
    }
  }

  #installBlockMenuOutsideClose(triggerEl) {
    if (this.blockMenuOutsideClose) {
      document.removeEventListener("mousedown", this.blockMenuOutsideClose, true)
    }
    this.blockMenuOutsideClose = (event) => {
      const target = event.target
      if (this.blockMenuTarget.contains(target)) return
      if (triggerEl && triggerEl.contains(target)) return
      this.#closeBlockMenu()
    }
    document.addEventListener("mousedown", this.blockMenuOutsideClose, true)
  }

  insertImage(event) {
    event?.preventDefault()
    if (!this.#requireHostedImages()) return
    this.#pickSingleImage().then((result) => {
      if (!result || !this.editor) return
      this.editor.chain().focus().insertNewsletterImage({ src: result.url, alt: "" }).run()
    })
  }

  insertImageRow(event) {
    event?.preventDefault()
    if (!this.#requireHostedImages()) return
    if (!this.hasMultiFileInputTarget) return
    this.multiFileInputTarget.value = ""
    this.multiFileInputTarget.onchange = async (changeEvent) => {
      const allFiles = Array.from(changeEvent.target.files || [])
      const files = allFiles.slice(0, MAX_IMAGE_ROW_ITEMS)
      if (allFiles.length > MAX_IMAGE_ROW_ITEMS) {
        window.alert(
          `Image rows hold up to ${MAX_IMAGE_ROW_ITEMS} images. Only the first ` +
          `${MAX_IMAGE_ROW_ITEMS} of your ${allFiles.length} selected images will be used.`
        )
      }
      if (files.length === 0 || !this.editor) return
      try {
        // Sequential upload (not Promise.all). Parallel POSTs to the same
        // newsletter race on `lock_version` via Active Storage's `attach`
        // which calls `record.save` — the second arrival hits
        // `StaleObjectError` and the user sees a spurious "upload failed".
        const uploads = []
        for (const file of files) {
          uploads.push(await this.#resolveImageSource(file))
        }
        const usable = uploads.filter((u) => u && u.url)
        if (usable.length === 0) return
        // Scale per-column widths so every image shares the same rendered
        // height — eliminates the height-mismatch gap that natural-aspect
        // images produce when sized to equal column widths.
        const items = aspectScaledRowItems(usable)
        this.editor.chain().focus().insertNewsletterImageRow({ srcs: items }).run()
      } catch (err) {
        console.error("[newsletter-editor] image-row upload failed:", err)
        window.alert("Could not upload one of the images. Please try again.")
      } finally {
        this.multiFileInputTarget.value = ""
      }
    }
    this.multiFileInputTarget.click()
  }

  // --- Newsletter-level settings -----------------------------------------

  setNewsletterBg(event) {
    const color = this.#colorFromEvent(event)
    if (!color) return
    if (this.hasBgColorInputTarget) this.bgColorInputTarget.value = color
    if (this.hasNewsletterBgSwatchTarget) this.newsletterBgSwatchTarget.style.background = color
    if (this.hasSurroundingsTarget) this.surroundingsTarget.style.background = color
    this.#closePaletteFor(event)
    this.#scheduleAutosave()
  }

  // Font is a per-selection mark (like size/color), not a newsletter-wide
  // setting. Empty value clears the override so the selection falls back to
  // the newsletter's default font on the wrapper. The doc change triggers
  // autosave via onUpdate, so no explicit schedule here.
  setFontFamily(event) {
    if (!this.editor) return
    const stack = event.target.value
    if (!stack) {
      this.editor.chain().focus().unsetFontFamily().run()
      return
    }
    this.editor.chain().focus().setFontFamily(stack).run()
  }

  triggerAutosave() {
    this.#scheduleAutosave()
  }

  // Toggles palette popovers within this controller's element.
  togglePalette(event) {
    event?.preventDefault()
    const trigger = event.currentTarget
    const key = trigger.dataset.paletteKey
    if (!key) return
    const palette = this.element.querySelector(`[data-palette-name="${key}"]`)
    if (!palette) return
    // Close all other palettes before opening this one.
    this.element
      .querySelectorAll("[data-palette-name]")
      .forEach((el) => {
        if (el !== palette) el.classList.add("hidden")
      })
    palette.classList.toggle("hidden")
  }

  // --- Private ------------------------------------------------------------

  #initialDocOrFallback() {
    const doc = this.initialContentValue
    if (doc && typeof doc === "object" && doc.type === "doc" && Array.isArray(doc.content)) {
      return doc
    }
    // Falling back to legacy body HTML preserves the draft when switching
    // from the legacy contenteditable editor to Tiptap.
    if (this.hasLegacyBodyValue && this.legacyBodyValue.trim()) {
      return this.legacyBodyValue
    }
    return { type: "doc", content: [{ type: "newsletterParagraph", content: [] }] }
  }

  #sync() {
    if (!this.editor) return
    if (this.hasContentJsonInputTarget) {
      this.contentJsonInputTarget.value = JSON.stringify(this.editor.getJSON())
    }
    if (this.hasBodyInputTarget) {
      this.bodyInputTarget.value = this.editor.getHTML()
    }
  }

  #refreshToolbarState() {
    if (!this.editor) return
    if (this.hasBoldButtonTarget) {
      this.boldButtonTarget.dataset.active = this.editor.isActive("bold") ? "true" : "false"
    }
    if (this.hasItalicButtonTarget) {
      this.italicButtonTarget.dataset.active = this.editor.isActive("italic") ? "true" : "false"
    }
    if (this.hasLinkButtonTarget) {
      this.linkButtonTarget.dataset.active = this.editor.isActive("link") ? "true" : "false"
    }
    if (this.hasFormatSelectTarget) {
      let value = "paragraph"
      for (const level of [1, 2, 3]) {
        if (this.editor.isActive("newsletterHeading", { level })) {
          value = `h${level}`
          break
        }
      }
      if (this.formatSelectTarget.value !== value) {
        this.formatSelectTarget.value = value
      }
    }
    if (this.hasTextColorSwatchTarget) {
      const color = this.editor.getAttributes("textStyle").color
      this.textColorSwatchTarget.style.background = color || "#000000"
    }
    if (this.hasHighlightSwatchTarget) {
      const color = this.editor.getAttributes("highlight").color
      this.highlightSwatchTarget.style.background = color || "#fff59d"
    }
    if (this.hasFontSizeSelectTarget) {
      const fontSize = this.editor.getAttributes("textStyle").fontSize
      const next = fontSize ? fontSize.replace(/px$/, "") : ""
      if (this.fontSizeSelectTarget.value !== next) {
        this.fontSizeSelectTarget.value = next
      }
    }
    if (this.hasFontFamilySelectTarget) {
      const fontFamily = this.editor.getAttributes("textStyle").fontFamily || ""
      if (this.fontFamilySelectTarget.value !== fontFamily) {
        this.fontFamilySelectTarget.value = fontFamily
      }
    }
    if (this.hasAlignLeftButtonTarget) {
      this.alignLeftButtonTarget.dataset.active = this.#isAligned("left") ? "true" : "false"
    }
    if (this.hasAlignCenterButtonTarget) {
      this.alignCenterButtonTarget.dataset.active = this.editor.isActive({ textAlign: "center" }) ? "true" : "false"
    }
    if (this.hasAlignRightButtonTarget) {
      this.alignRightButtonTarget.dataset.active = this.editor.isActive({ textAlign: "right" }) ? "true" : "false"
    }
  }

  // Left is default — active when neither center nor right is set, or when
  // textAlign is explicitly "left".
  #isAligned(direction) {
    if (direction === "left") {
      if (this.editor.isActive({ textAlign: "left" })) return true
      return !this.editor.isActive({ textAlign: "center" }) &&
             !this.editor.isActive({ textAlign: "right" })
    }
    return this.editor.isActive({ textAlign: direction })
  }

  #colorFromEvent(event) {
    const target = event?.currentTarget
    if (!target) return null
    return target.dataset?.color || target.value || null
  }

  #closePaletteFor(event) {
    const palette = event?.currentTarget?.closest("[data-palette-name]")
    palette?.classList.add("hidden")
  }

  // --- Autosave -----------------------------------------------------------

  #scheduleAutosave() {
    if (!this.hasAutosaveUrlValue || !this.autosaveUrlValue) return
    if (this.autosaveTimer) clearTimeout(this.autosaveTimer)
    this.autosaveTimer = setTimeout(() => this.#performAutosave(), 1500)
  }

  async #performAutosave() {
    if (!this.editor || !this.autosaveUrlValue) return
    if (this.autosaveInFlight) {
      this.autosavePending = true
      return
    }
    this.autosaveInFlight = true
    this.#setSaveStatus("Saving…")

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ""
    const body = new FormData()
    body.append("newsletter[content_json]", JSON.stringify(this.editor.getJSON()))
    body.append("newsletter[body]", this.editor.getHTML())
    body.append("newsletter[lock_version]", String(this.lockVersionValue))
    if (this.hasSubjectInputTarget) {
      body.append("newsletter[subject]", this.subjectInputTarget.value || "")
    }
    if (this.hasFontFamilyInputTarget) {
      body.append("newsletter[font_family]", this.fontFamilyInputTarget.value || "")
    }
    if (this.hasBgColorInputTarget) {
      body.append("newsletter[bg_color]", this.bgColorInputTarget.value || "")
    }
    // Unsubscribe is mandatory — never autosave it off.
    body.append("newsletter[show_unsubscribe_link]", "1")

    try {
      const response = await fetch(this.autosaveUrlValue, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": csrf,
          "X-Requested-With": "XMLHttpRequest",
        },
        body,
      })

      if (response.status === 409) {
        const payload = await response.json().catch(() => ({}))
        if (payload.error === "taken_over") {
          // A colleague has the editor now; editing_claim_controller says so.
          this.#setSaveStatus("Not saved")
          document.dispatchEvent(new CustomEvent("editing-claim:taken-over", { detail: { editor: payload.editor } }))
          return
        }
        if (typeof payload.lock_version === "number") {
          this.lockVersionValue = payload.lock_version
        }
        this.#setSaveStatus("Saved by another tab — reload")
        return
      }

      if (!response.ok) {
        this.#setSaveStatus("Save failed")
        return
      }

      const payload = await response.json().catch(() => ({}))
      if (typeof payload.lock_version === "number") {
        this.lockVersionValue = payload.lock_version
      }
      const stamp = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      this.#setSaveStatus(`Saved ${stamp}`)
    } catch (err) {
      console.error("[newsletter-editor] autosave error:", err)
      this.#setSaveStatus("Save failed")
    } finally {
      this.autosaveInFlight = false
      if (this.autosavePending) {
        this.autosavePending = false
        this.#scheduleAutosave()
      }
    }
  }

  #setSaveStatus(text) {
    if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = text
  }

  #pickSingleImage() {
    return new Promise((resolve) => {
      if (!this.hasFileInputTarget) {
        resolve(null)
        return
      }
      this.fileInputTarget.value = ""
      this.fileInputTarget.onchange = async (changeEvent) => {
        const file = changeEvent.target.files?.[0]
        this.fileInputTarget.value = ""
        if (!file) {
          resolve(null)
          return
        }
        try {
          const src = await this.#resolveImageSource(file)
          resolve(src)
        } catch (err) {
          console.error("[newsletter-editor] image upload failed:", err)
          window.alert("Could not upload image. Please try again.")
          resolve(null)
        }
      }
      this.fileInputTarget.click()
    })
  }

  // Image insertion is only allowed once the newsletter has a persisted
  // upload endpoint. `#requireHostedImages` guarantees that — so by the time
  // we get here we always have a hosted upload URL.
  async #resolveImageSource(file) {
    return uploadCompressedImage(file, this.imageUploadUrlValue)
  }

  // Returns true if the editor is ready to upload images to the server. If
  // not (brand-new unsaved draft), warns the user and bails. We intentionally
  // do NOT fall back to `data:` URLs — they get stripped or blocked by most
  // email clients and would silently break the delivered newsletter.
  #requireHostedImages() {
    if (this.hasImageUploadUrlValue && this.imageUploadUrlValue) return true
    window.alert(
      "Save the newsletter draft before adding images. Images need to be " +
      "uploaded to the server so they render in delivered emails."
    )
    return false
  }

  // Visually disables the image insertion buttons on unsaved drafts so the
  // unavailable state is obvious before the user clicks.
  #refreshImageInsertionAvailability() {
    const available = this.hasImageUploadUrlValue && !!this.imageUploadUrlValue
    if (!this.hasImageButtonTarget) return
    this.imageButtonTargets.forEach((btn) => {
      btn.disabled = !available
      btn.dataset.imagesAvailable = available ? "true" : "false"
      if (!available) {
        btn.setAttribute(
          "title",
          "Save the draft first to enable image uploads"
        )
      }
    })
  }

  // Flip `data-stuck` on the toolbar when the sentinel above it scrolls out
  // of view — used by Tailwind to drop the rounded top corners while pinned.
  #wireStickyToolbar() {
    if (!this.hasToolbarTarget || !this.hasToolbarSentinelTarget) return
    if (typeof IntersectionObserver === "undefined") return
    this.toolbarObserver = new IntersectionObserver(
      ([entry]) => {
        this.toolbarTarget.dataset.stuck = entry.isIntersecting ? "false" : "true"
      },
      { rootMargin: "0px", threshold: 0 }
    )
    this.toolbarObserver.observe(this.toolbarSentinelTarget)
  }

  // Registers the storage callback that image-row NodeViews invoke when the
  // "+ image" affordance is clicked. We pop a single-file picker and dispatch
  // addRowImage with the resulting hosted URL + natural dimensions so the
  // command can rebalance widths to match aspect ratios.
  #wireRowAddCallback() {
    const storage = this.editor?.storage?.newsletterImageRow
    if (!storage) return
    storage.onAddImageClick = async (rowPos) => {
      const upload = await this.#pickSingleImage()
      if (!upload || !this.editor) return
      this.editor.chain().focus().addRowImage(rowPos, {
        src: upload.url,
        alt: "",
        width: upload.width,
        height: upload.height,
      }).run()
    }
  }
}
